import React, { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { RootState } from 'typesafe-actions'

import { Box, Flex, Header, ToggleButton, ToggleButtonGroup } from '@driftt/dds'
import { access } from '@driftt/traffic-control'

import AccountEngagementTable from 'components/AccountEngagementTable'
import { CurrentUserContext } from 'context/currentUserContext'
import { segmentTrack, TrackType } from 'utils/tracking'

import {
  AccountOpportunityContext,
  AccountOpportunityFilter,
  AccountOpportunityFilterType
} from './AccountOpportunityContext'

export interface AccountEngagementOverviewProps {
  setDataSize: (size: number | undefined) => void
  setResultSize: (size: number | undefined) => void
}

const AccountEngagementOverview = ({
  setDataSize,
  setResultSize
}: AccountEngagementOverviewProps) => {
  const [dataLength, setDataLength] = useState<number | undefined>(undefined)
  const { setOpportunities } = useContext(AccountOpportunityContext)
  const currentUser = useContext(CurrentUserContext)
  const hasSfdc: boolean = useSelector((state: RootState) => state.integrations.status.salesforce)
  const hasOpportunities = access.hasGates(['sfdc_opportunities_enabled']) && hasSfdc
  const tableRef = React.useRef<HTMLDivElement>(null)
  useEffect(() => {
    const currentTableRef = tableRef.current
    if (currentTableRef) {
      window.addEventListener('resize', () => {
        currentTableRef.scrollIntoView()
      })
    }
  }, [tableRef])

  useEffect(() => {
    if (dataLength) {
      setDataSize(dataLength)
    }
  }, [dataLength, setDataSize])

  return (
    <Box
      css={{
        gridColumn: 'span 6',
        display: 'flex',
        flexDirection: 'column'
      }}>
      <Box
        ref={tableRef}
        css={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '100%',
          overflow: 'inherit',
          boxShadow: '$2',
          position: 'relative'
        }}>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          css={{
            height: '$8',
            padding: '$2',
            backgroundColor: '$white'
          }}>
          <Header variant="h3">Account engagement</Header>
          {hasOpportunities && (
            <ToggleButtonGroup
              color="blue"
              appearance="outline"
              initialSelectedValues={[AccountOpportunityFilter.ALL]}
              exclusive
              onChange={([val]) => {
                let event
                setOpportunities(val as AccountOpportunityFilterType)

                switch (val) {
                  case AccountOpportunityFilter.ALL:
                    event = TrackType.SFDC_ALL_OPPORTUNITIES_FILTER_CLICKED
                    break
                  case AccountOpportunityFilter.OPEN:
                    event = TrackType.SFDC_OPEN_OPPORTUNITIES_FILTER_CLICKED
                    break
                  case AccountOpportunityFilter.NONE:
                    event = TrackType.SDFC_NO_OPPORTUNITIES_FILTER_CLICKED
                    break
                  default:
                    break
                }

                if (event) {
                  segmentTrack(event, {
                    userId: currentUser?.id,
                    orgId: currentUser?.orgId
                  })
                }
              }}>
              <ToggleButton value={AccountOpportunityFilter.ALL}>All</ToggleButton>
              <ToggleButton value={AccountOpportunityFilter.OPEN}>Open opportunities</ToggleButton>
              <ToggleButton value={AccountOpportunityFilter.NONE}>No opportunities</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Flex>
        <AccountEngagementTable setDataLength={setDataLength} setResultSize={setResultSize} />
      </Box>
    </Box>
  )
}

export default AccountEngagementOverview
